import styled, { css } from 'styled-components';

import { FONT_NAMES, FONT_WEIGHT } from 'core/styles/fonts';
import * as mx from 'core/styles/mixins';
import * as vr from 'core/styles/variables';
import { displaySmall } from 'core/styles/Typography/Typography.style';
import { Content } from 'core/components/Tooltip/Tooltip.style';
import { Icon } from 'core/components/Icon';

import { Ul } from '../../Header.style';

export const Nav = styled.nav`
	${displaySmall}
	${mx.zIndex('middle')}

	width: 100%;
	margin: 0;
	border-top: 1px solid ${({ theme }) => theme.global.borders.lightest};
	background-color: ${({ theme }) =>
		theme.global.page.authenticatedHeader.color};
	box-shadow: 0 3px 5px ${vr.borderGrey};
`;

export const AccountSwitcherTooltip = styled(Content as any)`
	font-size: 12px;
	visibility: hidden;
	box-sizing: content-box;
	display: flex;
	align-items: center;

	position: absolute;
	z-index: 1;

	width: 196px;
	opacity: 0.9;
	border-radius: 3px;
	color: ${({ theme }) => theme.global.page.authenticatedHeader.color};
	background-color: ${({ theme }) => theme.global.typography.colors.default};
	box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.2);
	top: 100%;
	text-align: center;
	line-height: 16px;
	letter-spacing: 0;
	padding: 10px 5px;
	font-family: ${FONT_NAMES.OPEN_SANS};
	font-weight: ${FONT_WEIGHT.LIGHT};

	&::after {
		content: '';
		position: absolute;
		opacity: 0.9;
		bottom: 100%;
		margin-left: -10px;
		left: 50%;
		border: 10px solid ${({ theme }) => theme.global.typography.colors.default};
		border-color: transparent transparent
			${({ theme }) => theme.global.typography.colors.default} transparent;
	}
`;

export const PrimaryNavList = styled.ul<{
	showAccountSwitcher: boolean;
}>`
	${mx.listReset}

	padding: 0 0 0 25px;
	width: 100%;
	text-align: left;
	height: 58px;

	> li {
		width: auto;
		height: 100%;
		display: inline-block;
		margin: 0;

		+ li {
			margin-left: 8px;
		}

		&::after {
			${mx.pseudo}
			width: 100%;
			height: 0;
			bottom: 0;
			left: 0;
			background-color: ${({ theme }) =>
				theme.global.page.authenticatedHeader.activeBar.iaColor};
			transition: height ${({ theme }) => theme.variables.baseTransition};
		}

		/* Menu item */
		a,
		button {
			${mx.buttonReset}

			width: 100%;
			display: block;
			text-decoration: none;
			color: ${({ theme }) =>
				theme.global.page.authenticatedHeader.primary.desktop.fontColor};
			font-size: 16px;
			font-family: ${FONT_NAMES.OPEN_SANS};
			font-weight: ${FONT_WEIGHT.LIGHT};
			line-height: 24px;
			padding: 20px 12px;
			border: none;
			background-color: inherit;

			&:hover {
				cursor: inherit;
				height: 58px;
			}
		}

		button {
			${mx.tabFocus}
			display: block;
			line-height: inherit;
			padding: 20px 12px;
		}

		&:first-child a {
			/** Remove the left and right padding for aligning the first level 1 nav with the logo */
			padding-left: 0;
			padding-right: 0;
		}

		&:hover {
			cursor: pointer;
			background-color: ${({ theme }) =>
				theme.global.page.authenticatedHeader.activeBar.iaColor};
		}

		&:first-child {
			&:hover {
				cursor: pointer;
				background-color: unset;
			}
		}

		&.desktop-nav--item.-active {
			::after {
				${mx.pseudo}
				height: 2px;
				-webkit-text-stroke-width: 2px;
				background-color: ${({ theme }) =>
					theme.global.page.authenticatedHeader.activeBar.iaStrokeColor};
				color: ${({ theme }) =>
					theme.global.page.authenticatedHeader.activeBar.iaFontColor};
			}
		}

		${props =>
			props.showAccountSwitcher &&
			css`
				&:first-child {
					height: 100%;

					&::after {
						${mx.pseudo}
						width: 100%;
						height: 0;
					}

					&:hover {
						cursor: pointer;

						${AccountSwitcherTooltip} {
							visibility: visible;
						}
					}
				}
			`}
	}
`;

export const SubNavList = styled(Ul as any)<{ isExpanded?: boolean }>`
	width: 240px;

	&& > li {
		a:hover {
			cursor: pointer;
			background-color: ${({ theme }) =>
				theme.global.page.authenticatedHeader.subItems.desktop.iaHoverColor};
		}

		a:focus {
			color: ${({ theme }) =>
				theme.global.page.authenticatedHeader.subItems.desktop
					.iaSelectedFontColor};
			background-color: ${({ theme }) =>
				theme.global.page.authenticatedHeader.subItems.desktop.iaSelectedColor};
		}
	}
`;

export const ArrowNavImage = styled(Icon as any)`
	height: 64px;
	position: absolute;
	left: 90%;
	margin-top: -20px;
	color: ${({ theme }) => theme.global.borders.hr};
`;

export const SwitchIcon = styled(Icon as any)`
	display: none;
`;

export const AccountSwitcherLink = styled.div<{
	arrowIconTop: boolean;
}>`
	color: ${({ theme }) => theme.global.typography.colors.active};
	display: flex;
	flex-direction: row;
	align-items: baseline;
	position: relative;

	${props =>
		props.arrowIconTop &&
		css`
			i {
				top: 6px;
			}
		`}

	&:hover {
		${SwitchIcon} {
			transform: rotate(90deg);
		}
	}
`;

export const ProductClassification = styled.label`
	font-weight: ${FONT_WEIGHT.SEMI_BOLD};
	&:hover {
		cursor: pointer;
	}
`;

export const AccountBalance = styled.label`
	font-weight: ${FONT_WEIGHT.REGULAR};
	margin-left: 3px;
	&:hover {
		cursor: pointer;
	}
`;

export const CenterContents = styled.div`
	position: absolute;
	right: -4px;
	top: 2px;

	i {
		animation: blink 1s;
		animation-iteration-count: infinite;
	}

	/* Firefox old */
	@-moz-keyframes blink {
		0% {
			opacity: 1;
		}
		50% {
			opacity: 0;
		}
		100% {
			opacity: 1;
		}
	}

	@-webkit-keyframes blink {
		0% {
			opacity: 1;
		}
		50% {
			opacity: 0;
		}
		100% {
			opacity: 1;
		}
	}

	/* IE */
	@-ms-keyframes blink {
		0% {
			opacity: 1;
		}
		50% {
			opacity: 0;
		}
		100% {
			opacity: 1;
		}
	}

	/* Opera and prob css3 final iteration */
	@keyframes blink {
		0% {
			opacity: 1;
		}
		50% {
			opacity: 0;
		}
		100% {
			opacity: 1;
		}
	}
`;

export const AccountSwitcherContentContainer = styled.div<{
	showIcon: boolean;
}>`
	display: flex;
	align-items: baseline;
	margin-right: 20px;

	${props =>
		props.showIcon &&
		css`
			${SwitchIcon} {
				display: block;
			}
		`}
`;

export const NavButton = styled.button`
	&:focus-visible {
		outline: -webkit-focus-ring-color auto 1px !important;
	}

	&:focus {
		color: ${({ theme }) =>
			theme.global.page.authenticatedHeader.selectedItem.fontColor};
		background-color: ${({ theme }) =>
			theme.global.page.authenticatedHeader.selectedItem.backgroundColor};
	}
`;
